import type { ProductionDailyOutputRow, ProductionLot, ProductionOrder, ProductionOrderStage } from './types'

export function completionPercent(actual: number, planned: number): number {
  if (!Number.isFinite(actual) || !Number.isFinite(planned) || planned <= 0) return 0
  return Math.max(0, Math.min(100, Math.round(actual / planned * 100)))
}

export type ProductionScheduleRisk = {
  level: 'critical' | 'warning'
  code: 'start_overdue' | 'incomplete_after_start'
  label: string
  daysOverdue: number
}

export type ProductionReportingLabels = {
  unassigned: string
  allStages: string
  allEmployees: string
  productionOutputTitle: string
  csvDate: string
  csvFilters: string
  csvRows: string
  csvCompleted: string
  csvRejected: string
  csvRework: string
  csvSequence: string
  csvStage: string
  csvStageCode: string
  csvEmployee: string
  csvEmployeeId: string
  csvProductionOrderHandover: string
  csvSnapshot: string
  csvOrder: string
  csvProduct: string
  csvStatus: string
  csvPlanned: string
  csvProduced: string
  csvRemaining: string
  csvCompletionPercent: string
  csvActiveLots: string
  csvCompletedLots: string
  csvStageSequence: string
  csvActual: string
  csvVariance: string
  csvYieldPercent: string
  csvAssignedEmployee: string
  csvLotNumber: string
  csvCurrentStage: string
  csvInitialQuantity: string
  csvCurrentQuantity: string
  csvExceptions: string
  csvCreatedAt: string
  handoverProductionOrder: string
  handoverSnapshot: string
  handoverStatus: string
  handoverPlan: string
  handoverActual: string
  handoverLots: string
  handoverActive: string
  handoverCompleted: string
  handoverTotal: string
  handoverExceptions: string
  handoverBottleneck: string
  handoverStageProgress: string
  handoverRejected: string
  handoverRework: string
  queueShiftSummary: string
  queueOrders: string
  queueTotalPlanned: string
  queueTotalProduced: string
  queueTotalRemaining: string
  queueOverallCompletionPercent: string
  queueMoCode: string
  queueDepartment: string
  queueScheduleRisk: string
  queueStartDate: string
  queueSourceWarehouse: string
  queueDestinationWarehouse: string
  queueEquipment: string
  queueSubmittedBy: string
  queueNotes: string
  queueFilterStage: string
  queueFilterEmployee: string
  riskStartOverdue: (days: number) => string
  riskIncompleteAfterStart: (days: number) => string
}

function startOfUtcDay(value: string | Date): number {
  const date = typeof value === 'string' ? new Date(`${value.slice(0, 10)}T00:00:00Z`) : value
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export function evaluateProductionScheduleRisk(
  order: ProductionOrder,
  now: Date = new Date(),
  labels?: Partial<Pick<ProductionReportingLabels, 'riskStartOverdue' | 'riskIncompleteAfterStart'>>,
): ProductionScheduleRisk | null {
  if (!order.start_date || order.status === 'completed' || order.status === 'cancelled') return null
  const start = startOfUtcDay(order.start_date)
  const today = startOfUtcDay(now)
  if (!Number.isFinite(start) || start >= today) return null
  const planned = Number(order.planned_quantity || 0)
  const produced = Number(order.produced_quantity || 0)
  const daysOverdue = Math.floor((today - start) / 86_400_000)
  if (order.status === 'planned' && produced <= 0) {
    return {
      level: 'critical',
      code: 'start_overdue',
      label: labels?.riskStartOverdue?.(daysOverdue) ?? `Start overdue by ${daysOverdue} day${daysOverdue === 1 ? '' : 's'}`,
      daysOverdue,
    }
  }
  if (produced < planned) {
    return {
      level: 'warning',
      code: 'incomplete_after_start',
      label: labels?.riskIncompleteAfterStart?.(daysOverdue) ?? `Incomplete ${daysOverdue} day${daysOverdue === 1 ? '' : 's'} after start`,
      daysOverdue,
    }
  }
  return null
}

export function summarizeProductionScheduleRisks(orders: ProductionOrder[], now: Date = new Date()) {
  return orders.reduce(
    (summary, order) => {
      const risk = evaluateProductionScheduleRisk(order, now)
      if (!risk) summary.onTrack += 1
      else summary[risk.level] += 1
      return summary
    },
    { all: orders.length, critical: 0, warning: 0, onTrack: 0 },
  )
}

export function sortProductionOrdersByPriority(
  orders: ProductionOrder[],
  now: Date = new Date(),
): ProductionOrder[] {
  const riskRank = (order: ProductionOrder) => {
    const level = evaluateProductionScheduleRisk(order, now)?.level
    return level === 'critical' ? 0 : level === 'warning' ? 1 : 2
  }
  const startRank = (order: ProductionOrder) => {
    if (!order.start_date) return Number.POSITIVE_INFINITY
    const parsed = Date.parse(`${order.start_date.slice(0, 10)}T00:00:00Z`)
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
  }
  return [...orders].sort((left, right) =>
    riskRank(left) - riskRank(right)
    || startRank(left) - startRank(right)
    || left.mo_code.localeCompare(right.mo_code),
  )
}

export type DepartmentOutputSnapshot = {
  department: string
  orders: number
  planned: number
  produced: number
  remaining: number
  completionPercent: number
  atRisk: number
}

export function groupProductionOutputByDepartment(
  orders: ProductionOrder[],
  now: Date = new Date(),
): DepartmentOutputSnapshot[] {
  const groups = new Map<string, Omit<DepartmentOutputSnapshot, 'completionPercent'>>()
  orders.forEach((order) => {
    const department = order.department_name || `Department #${order.department_agent_id}`
    const current = groups.get(department) || { department, orders: 0, planned: 0, produced: 0, remaining: 0, atRisk: 0 }
    const planned = Number(order.planned_quantity || 0)
    const produced = Number(order.produced_quantity || 0)
    current.orders += 1
    current.planned += planned
    current.produced += produced
    current.remaining += Math.max(0, planned - produced)
    if (evaluateProductionScheduleRisk(order, now)) current.atRisk += 1
    groups.set(department, current)
  })
  return [...groups.values()]
    .map((group) => ({ ...group, completionPercent: completionPercent(group.produced, group.planned) }))
    .sort((left, right) => right.atRisk - left.atRisk || left.department.localeCompare(right.department))
}

export function stageCompletionPercent(stage: ProductionOrderStage): number {
  return completionPercent(Number(stage.completed_quantity || 0), Number(stage.planned_quantity || 0))
}

export function findBottleneckStage(stages: ProductionOrderStage[]): ProductionOrderStage | null {
  return stages
    .filter((stage) => Number(stage.planned_quantity) > 0 && stage.status !== 'completed')
    .reduce<ProductionOrderStage | null>((lowest, stage) => {
      if (!lowest) return stage
      const difference = stageCompletionPercent(stage) - stageCompletionPercent(lowest)
      if (difference < 0) return stage
      if (difference === 0 && stage.operation_sequence < lowest.operation_sequence) return stage
      return lowest
    }, null)
}

export function summarizeStageExceptions(stages: ProductionOrderStage[]) {
  return stages.reduce(
    (summary, stage) => ({
      rejected: summary.rejected + Number(stage.rejected_quantity || 0),
      rework: summary.rework + Number(stage.rework_quantity || 0),
    }),
    { rejected: 0, rework: 0 },
  )
}

export function lotExceptionCount(lot: ProductionLot): number {
  return lot.stages?.reduce(
    (total, stage) => total + Number(stage.rejected_quantity || 0) + Number(stage.rework_quantity || 0),
    0,
  ) ?? 0
}

export function paginateRows<T>(rows: T[], page: number, perPage: number): T[] {
  const safePage = Math.max(1, Math.floor(page))
  const safePerPage = Math.max(1, Math.floor(perPage))
  return rows.slice((safePage - 1) * safePerPage, safePage * safePerPage)
}

function csvCell(value: unknown): string {
  const text = String(value ?? '')
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function csvRow(values: unknown[]): string {
  return values.map(csvCell).join(',')
}

export function filterDailyOutputRows(
  rows: ProductionDailyOutputRow[],
  stageCode = '',
  employeeId = '',
): ProductionDailyOutputRow[] {
  return rows.filter((row) =>
    (!stageCode || row.stage_code === stageCode)
    && (!employeeId || String(row.employee_id ?? 'unassigned') === employeeId),
  )
}

export function summarizeDailyOutput(rows: ProductionDailyOutputRow[]) {
  return rows.reduce(
    (summary, row) => ({
      completed: summary.completed + Number(row.completed_quantity || 0),
      rejected: summary.rejected + Number(row.rejected_quantity || 0),
      rework: summary.rework + Number(row.rework_quantity || 0),
    }),
    { completed: 0, rejected: 0, rework: 0 },
  )
}

export function buildDailyOutputCsv(
  rows: ProductionDailyOutputRow[],
  date: string,
  filterDescription: string,
  labels?: Partial<ProductionReportingLabels>,
): string {
  const totals = summarizeDailyOutput(rows)
  const csvRows: unknown[][] = [
    [labels?.productionOutputTitle ?? 'Daily production output'],
    [labels?.csvDate ?? 'Date', date],
    [labels?.csvFilters ?? 'Filters', filterDescription],
    [labels?.csvRows ?? 'Rows', rows.length],
    [labels?.csvCompleted ?? 'Completed', totals.completed],
    [labels?.csvRejected ?? 'Rejected', totals.rejected],
    [labels?.csvRework ?? 'Rework', totals.rework],
    [],
    [
      labels?.csvSequence ?? 'Sequence',
      labels?.csvStage ?? 'Stage',
      labels?.csvStageCode ?? 'Stage code',
      labels?.csvEmployee ?? 'Employee',
      labels?.csvEmployeeId ?? 'Employee ID',
      labels?.csvCompleted ?? 'Completed',
      labels?.csvRejected ?? 'Rejected',
      labels?.csvRework ?? 'Rework',
    ],
    ...rows.map((row) => [
      row.operation_sequence,
      row.stage_name,
      row.stage_code,
      row.employee_name || labels?.unassigned || 'Unassigned',
      row.employee_id ?? '',
      row.completed_quantity,
      row.rejected_quantity,
      row.rework_quantity,
    ]),
  ]
  return `\uFEFF${csvRows.map(csvRow).join('\r\n')}\r\n`
}

export function buildProductionOrderCsv(
  order: ProductionOrder,
  stages: ProductionOrderStage[],
  lots: ProductionLot[],
  snapshot: string,
  labels?: Partial<ProductionReportingLabels>,
): string {
  const exceptions = summarizeStageExceptions(stages)
  const produced = Number(order.produced_quantity || 0)
  const planned = Number(order.planned_quantity || 0)
  const rows: unknown[][] = [
    [labels?.csvProductionOrderHandover ?? 'Production order handover'],
    [labels?.csvSnapshot ?? 'Snapshot', snapshot],
    [labels?.csvOrder ?? 'Order', order.mo_code],
    [labels?.csvProduct ?? 'Product', order.product_name || `Product #${order.product_id}`],
    [labels?.csvStatus ?? 'Status', order.status],
    [labels?.csvPlanned ?? 'Planned', planned],
    [labels?.csvProduced ?? 'Produced', produced],
    [labels?.csvRemaining ?? 'Remaining', Math.max(0, planned - produced)],
    [labels?.csvCompletionPercent ?? 'Completion percent', completionPercent(produced, planned)],
    [labels?.csvRejected ?? 'Rejected', exceptions.rejected],
    [labels?.csvRework ?? 'Rework', exceptions.rework],
    [labels?.csvActiveLots ?? 'Active lots', lots.filter((lot) => lot.status === 'active').length],
    [labels?.csvCompletedLots ?? 'Completed lots', lots.filter((lot) => lot.status === 'completed').length],
    [],
    [
      labels?.csvStageSequence ?? 'Stage sequence',
      labels?.csvStage ?? 'Stage',
      'Code',
      labels?.csvStatus ?? 'Status',
      labels?.csvPlanned ?? 'Planned',
      labels?.csvActual ?? 'Actual',
      labels?.csvVariance ?? 'Variance',
      labels?.csvYieldPercent ?? 'Yield percent',
      labels?.csvRejected ?? 'Rejected',
      labels?.csvRework ?? 'Rework',
      labels?.csvAssignedEmployee ?? 'Assigned employee',
    ],
    ...stages.map((stage) => [
      stage.operation_sequence,
      stage.stage_name,
      stage.stage_code,
      stage.status,
      stage.planned_quantity,
      stage.completed_quantity,
      Math.max(0, Number(stage.planned_quantity) - Number(stage.completed_quantity)),
      stageCompletionPercent(stage),
      stage.rejected_quantity,
      stage.rework_quantity,
      stage.assigned_employee_name || '',
    ]),
    [],
    [
      labels?.csvLotNumber ?? 'Lot number',
      labels?.csvStatus ?? 'Status',
      labels?.csvCurrentStage ?? 'Current stage',
      labels?.csvInitialQuantity ?? 'Initial quantity',
      labels?.csvCurrentQuantity ?? 'Current quantity',
      labels?.csvExceptions ?? 'Exceptions',
      labels?.csvCreatedAt ?? 'Created at',
    ],
    ...lots.map((lot) => [
      lot.lot_number,
      lot.status,
      lot.current_stage_code,
      lot.initial_quantity,
      lot.current_quantity,
      lotExceptionCount(lot),
      lot.created_at || '',
    ]),
  ]
  return `\uFEFF${rows.map(csvRow).join('\r\n')}\r\n`
}

export function buildHandoverText(
  order: ProductionOrder,
  stages: ProductionOrderStage[],
  lots: ProductionLot[],
  snapshot: string,
  labels?: Partial<ProductionReportingLabels>,
): string {
  const planned = Number(order.planned_quantity || 0)
  const produced = Number(order.produced_quantity || 0)
  const exceptions = summarizeStageExceptions(stages)
  const bottleneck = findBottleneckStage(stages)
  const lines = [
    `${labels?.handoverProductionOrder ?? 'Production order'} ${order.mo_code}`,
    order.product_name || `Product #${order.product_id}`,
    `${labels?.handoverSnapshot ?? 'Snapshot'}: ${snapshot}`,
    `${labels?.handoverStatus ?? 'Status'}: ${order.status.replace(/_/g, ' ')}`,
    `${labels?.handoverPlan ?? 'Plan'}: ${planned} | ${labels?.handoverActual ?? 'Actual'}: ${produced} | ${labels?.csvRemaining ?? 'Remaining'}: ${Math.max(0, planned - produced)} | ${labels?.csvCompletionPercent ?? 'Completion percent'}: ${completionPercent(produced, planned)}%`,
    `${labels?.handoverLots ?? 'Lots'}: ${lots.filter((lot) => lot.status === 'active').length} ${labels?.handoverActive ?? 'active'}, ${lots.filter((lot) => lot.status === 'completed').length} ${labels?.handoverCompleted ?? 'completed'}, ${lots.length} ${labels?.handoverTotal ?? 'total'}`,
    `${labels?.handoverExceptions ?? 'Exceptions'}: ${exceptions.rejected} ${labels?.handoverRejected ?? 'rejected'}, ${exceptions.rework} ${labels?.handoverRework ?? 'rework'}`,
  ]
  if (bottleneck) {
    lines.push(`${labels?.handoverBottleneck ?? 'Bottleneck'}: ${bottleneck.stage_name} (${stageCompletionPercent(bottleneck)}%)`)
  }
  lines.push('', `${labels?.handoverStageProgress ?? 'Stage progress'}:`)
  stages.forEach((stage) => {
    lines.push(
      `${stage.operation_sequence}. ${stage.stage_name}: ${stage.completed_quantity}/${stage.planned_quantity} (${stageCompletionPercent(stage)}%), ${labels?.handoverRejected ?? 'rejected'} ${stage.rejected_quantity}, ${labels?.handoverRework ?? 'rework'} ${stage.rework_quantity}${stage.assigned_employee_name ? `, ${stage.assigned_employee_name}` : ''}`,
    )
  })
  return `${lines.join('\n')}\n`
}

export function buildProductionQueueCsv(
  orders: ProductionOrder[],
  snapshot: string,
  filterDescription: string,
  labels?: Partial<ProductionReportingLabels>,
): string {
  const totalPlanned = orders.reduce((total, order) => total + Number(order.planned_quantity || 0), 0)
  const totalProduced = orders.reduce((total, order) => total + Number(order.produced_quantity || 0), 0)
  const rows: unknown[][] = [
    [labels?.queueShiftSummary ?? 'Production queue shift summary'],
    [labels?.csvSnapshot ?? 'Snapshot', snapshot],
    [labels?.csvFilters ?? 'Filters', filterDescription],
    [labels?.queueOrders ?? 'Orders', orders.length],
    [labels?.queueTotalPlanned ?? 'Total planned', totalPlanned],
    [labels?.queueTotalProduced ?? 'Total produced', totalProduced],
    [labels?.queueTotalRemaining ?? 'Total remaining', Math.max(0, totalPlanned - totalProduced)],
    [labels?.queueOverallCompletionPercent ?? 'Overall completion percent', completionPercent(totalProduced, totalPlanned)],
    [],
    [
      labels?.queueMoCode ?? 'MO code',
      labels?.csvProduct ?? 'Product',
      labels?.queueDepartment ?? 'Department',
      labels?.csvStatus ?? 'Status',
      labels?.queueScheduleRisk ?? 'Schedule risk',
      labels?.csvPlanned ?? 'Planned',
      labels?.csvProduced ?? 'Produced',
      labels?.csvRemaining ?? 'Remaining',
      labels?.csvCompletionPercent ?? 'Completion percent',
      labels?.queueStartDate ?? 'Start date',
      labels?.queueSourceWarehouse ?? 'Source warehouse',
      labels?.queueDestinationWarehouse ?? 'Destination warehouse',
      labels?.queueEquipment ?? 'Equipment',
      labels?.queueSubmittedBy ?? 'Submitted by',
      labels?.queueNotes ?? 'Notes',
    ],
    ...orders.map((order) => {
      const planned = Number(order.planned_quantity || 0)
      const produced = Number(order.produced_quantity || 0)
      return [
        order.mo_code,
        order.product_name || `Product #${order.product_id}`,
        order.department_name || `#${order.department_agent_id}`,
        order.status,
        evaluateProductionScheduleRisk(order, new Date(snapshot), labels)?.label || '',
        planned,
        produced,
        Math.max(0, planned - produced),
        completionPercent(produced, planned),
        order.start_date || '',
        order.source_warehouse_name || '',
        order.destination_warehouse_name || '',
        order.knitting_equipment_name || '',
        order.submitted_by_user_name || '',
        order.notes || '',
      ]
    }),
  ]
  return `\uFEFF${rows.map(csvRow).join('\r\n')}\r\n`
}
